import type { ReactNode } from 'react'
import { Link } from 'react-router'
import { useHitTarget } from '../../platform/responsive/hitTarget'
import { Money } from '../../platform/primitives/Money'
import { accountTypeLabel } from './accountGroups'
import { formatRelative } from './accountsStaleLabels'
import type { GroupHealth, InstitutionGroup } from './connectionGroups'
import { StatusPill } from './connectionStatus'
import './accounts.css'

/**
 * One bank connection, rendered open — accounts visible without interaction
 * (design decision 3, spec §A3).
 *
 * **Presentational.** Takes its data and its actions; fetches nothing. The
 * route component owns `useProviderItems`/`useAccounts` and passes the
 * already-joined group down.
 *
 * **No card-level money total.** `/accounts` is the screen about money;
 * this one is about plumbing, and a total here is just `/accounts` again but
 * worse (spec §A3).
 */
export function ConnectionCard({
  group,
  health,
  matching,
  actions,
}: {
  group: InstitutionGroup
  health: GroupHealth | null
  /** A reconnect for this disconnected bank is under way and waits to be matched: the card's own
   *  action is Match accounts, so the note must not say to connect again later. */
  matching?: boolean
  actions: ReactNode
}) {
  // No item, no Reconnect action (`actionsFor` returns null), so no note
  // claiming the user should reconnect: it would be a dead end (#425 S4).
  const reauth = health?.kind === 'reauth' && group.item !== null
  // M0-R29, found by the seeded browser sweep (Task 6): this anchor carried no
  // min-height at all and measured 40px against the 44px floor below 640px —
  // the same defect #423 found on `button.list-row__main`, one component over.
  // `useHitTarget`, not a CSS min-height or a media query: CLAUDE.md's one
  // breakpoint source is `useBreakpoint()`, and `single-source.test.ts` forbids
  // a layout media query in ANY stylesheet with no exemption — a CSS-only fix
  // was never available here, unlike what the account row's LACK of an inline
  // style might suggest at a glance. `'inline'`, matching `InlineLink`'s own
  // size: this is a standalone link in a dense list, not a page-level control.
  const accountLinkMinHeight = useHitTarget('inline')

  return (
    <section className="connection-card" role="listitem" aria-label={group.name}>
      <div className="connection-card__head">
        <span className="connection-card__logo" aria-hidden="true">
          {group.logoUrl ? <img src={group.logoUrl} alt="" /> : initials(group.name)}
        </span>
        <span className="connection-card__id">
          <span className="connection-card__name">{group.name}</span>
          <span className="connection-card__sub">{syncLine(group)}</span>
        </span>
        <StatusPill health={health} />
      </div>

      {/* M5-R13: a reauth-required connection is stale-with-its-date, never an
          error — the same `.note--stale` class AccountDetail's HealthNote and
          Accounts.tsx's RowHealthNote already use, wherever it is shown. */}
      {reauth && (
        <p className="note note--stale connection-card__note">
          {`${group.name} needs reconnecting — balances and transactions have stopped updating.`}
        </p>
      )}

      {/* A plain `.note` (not `.note--stale`) — this is not a warning about
          something the user should act on, it is a fact. `actionsFor` gives a
          disconnected group at most a Reconnect action, never Sync or Disconnect
          — and the note says which case this is. `connection_status` is the
          only source, so it reads the same whether the items endpoint lists
          the item (provider-removed) or not (user-disconnected, or a dangling
          row with a null status). */}
      {health?.kind === 'disconnected' && (
        <p className="note connection-card__note">
          {matching === true
            ? 'Disconnected. These accounts keep their history and are no longer updating. ' +
              'Match them to the new connection to finish reconnecting.'
            : health.reconnectable
            ? // The status line says WHICH Reconnect this is: a disconnected bank
              // signs in again as a new connection that takes the accounts over —
              // not the repair an errored connection's note offers.
              'Disconnected. These accounts keep their history and are no longer updating. ' +
              'Reconnect to pick them up again.'
            : 'Disconnected. These accounts keep their history and are no longer updating. You can ' +
              'connect this bank again later.'}
        </p>
      )}

      {group.accounts.length === 0 ? (
        <p className="connection-card__empty">
          No accounts yet. This usually takes a minute — if it doesn&apos;t, sync it yourself.
        </p>
      ) : (
        <ul className="connection-card__accounts" aria-label="Accounts">
          {group.accounts.map((a) => (
            <li key={a.id} className="connection-card__account">
              <Link
                to={`/accounts/${a.id}`}
                className="connection-card__account-link hoverable"
                style={{ minHeight: accountLinkMinHeight }}
              >
                <span className="connection-card__account-name">{a.name}</span>
                <span className="connection-card__account-meta">
                  {[accountTypeLabel(a.type), a.mask ? `·· ${a.mask}` : null]
                    .filter(Boolean)
                    .join(' ')}
                </span>
              </Link>
              {a.currentCents !== null && (
                <Money cents={a.currentCents} currency={a.currency} kind="stock" />
              )}
            </li>
          ))}
        </ul>
      )}

      <div className="connection-card__actions">{actions}</div>
    </section>
  )
}

function initials(name: string): string {
  return name
    .split(/\s+/)
    .slice(0, 2)
    .map((w) => [...w][0] ?? '')
    .join('')
    .toUpperCase()
}

function syncLine(group: InstitutionGroup): string {
  if (group.item === null) {
    const n = group.accounts.length
    return `${n} account${n === 1 ? '' : 's'}`
  }
  return group.item.lastSyncedAt ? `Synced ${formatRelative(group.item.lastSyncedAt)}` : 'Never synced'
}
